import { API_BASE, COMPANY_ID, browserCanReachKit } from '@/lib/kit-client';
import { visitorId } from '@/lib/visitor-id';

/**
 * Conversion reporting for KIT A/B tests.
 *
 * `POST /experiments/convert { visitorId, goal }` only flips visitors who were
 * already assigned to a running experiment with that goal, and ignores
 * everyone else. We still avoid calling it for visitors who were never
 * assigned a variant: `markAssigned` records which goals are live for this
 * visitor, and `trackConversion` stays silent for any other goal. With no
 * running experiment the site therefore sends nothing at all.
 */

export type ExperimentGoal = 'lead' | 'cta_click' | 'add_to_cart' | 'booking';

const KEY = 'kit_ab_goals';
const API_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';

function read(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || '[]');
  } catch {
    return [];
  }
}

/** Remember that this visitor is in an experiment whose win condition is `goal`. */
export function markAssigned(goal: string) {
  try {
    const goals = new Set(read());
    goals.add(goal);
    sessionStorage.setItem(KEY, JSON.stringify([...goals]));
  } catch {
    /* storage blocked: conversions for this visit are simply not reported */
  }
}

/** Report a win. Fire-and-forget; never throws and never blocks the visitor. */
export function trackConversion(goal: ExperimentGoal) {
  if (!API_KEY || !browserCanReachKit() || !read().includes(goal)) return;
  const vid = visitorId();
  if (!vid) return; // no visitor id: analytics is not allowed for this visitor
  try {
    void fetch(`${API_BASE}/api/${COMPANY_ID}/experiments/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-company-key': API_KEY },
      body: JSON.stringify({ visitorId: vid, goal }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* telemetry must never surface to the visitor */
  }
}
